export type BillingMilestonePlanAmountInput = {
  type: string;
  fixedAmount?: unknown;
  calculatedAmount?: unknown;
  percentage?: unknown;
  requiresEconomicReview?: boolean;
  invoiceAmount?: unknown;
};

function amountCents(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  const normalized = String(value).trim();
  if (!/^\d+(?:\.\d{1,2})?$/.test(normalized)) return null;
  const amount = Number(normalized);
  return Number.isFinite(amount) ? Math.round(amount * 100) : null;
}

function percentageValue(value: unknown): number | null {
  if (typeof value !== "number" && typeof value !== "string") return null;
  const normalized = String(value).trim();
  if (!/^\d+(?:\.\d+)?$/.test(normalized)) return null;
  const percentage = Number(normalized);
  return Number.isFinite(percentage) && percentage > 0 && percentage <= 100
    ? percentage
    : null;
}

/**
 * Resolve only the planned obligation for a milestone. Invoice totals are deliberately
 * ignored: they can include VAT/withholding and are not contractual plan amounts.
 */
export function resolvePlannedBillingMilestoneAmount(
  milestone: BillingMilestonePlanAmountInput,
): number | null {
  if (milestone.requiresEconomicReview === true) return null;

  if (milestone.type === "fixed_amount") {
    const cents = amountCents(milestone.fixedAmount);
    return cents === null ? null : cents / 100;
  }

  if (milestone.type === "monthly_certification") {
    const cents = amountCents(milestone.calculatedAmount);
    return cents === null ? null : cents / 100;
  }

  if (milestone.type !== "percentage") return null;
  const baseCents = amountCents(milestone.fixedAmount);
  const percentage = percentageValue(milestone.percentage);
  if (baseCents === null || percentage === null) return null;

  const calculatedCents = amountCents(milestone.calculatedAmount);
  const calculationWasProvided = milestone.calculatedAmount !== null
    && milestone.calculatedAmount !== undefined
    && !(typeof milestone.calculatedAmount === "string" && milestone.calculatedAmount.trim() === "");
  const expectedCents = Math.round((baseCents * percentage) / 100);
  if (calculationWasProvided && (calculatedCents === null || calculatedCents !== expectedCents)) return null;
  return expectedCents / 100;
}
